import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface TopBarProps {
  /** Leading slot — the sidebar toggle. Compose from TopBarButton. */
  leading?: ReactNode;
  /** Centred page title from the active route. */
  title?: ReactNode;
  /** Decorative icon before the centred title. */
  titleIcon?: ReactNode;
  /** Right-aligned action nodes — upload indicator, notifications, avatar… */
  actions?: ReactNode;
  className?: string;
}

/**
 * Portal top bar — 52px shell chrome on the shell tokens.
 *
 *   <TopBar
 *     leading={<TopBarButton aria-label="Toggle navigation"><MenuIcon className="size-4" /></TopBarButton>}
 *     titleIcon={<AssignmentIcon className="size-4" aria-hidden="true" />}
 *     title="Sites"
 *     actions={<NotificationsButton />}
 *   />
 *
 * The title is absolutely centred across the whole bar (pointer-events
 * none, like the portal), so leading/actions never push it off-centre;
 * actions keep a stacking context above it.
 */
export const TopBar = forwardRef<HTMLElement, TopBarProps>(
  ({ leading, title, titleIcon, actions, className }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(
          "relative flex h-(--shell-topbar-height) shrink-0 items-center gap-1 border-b border-shell-border bg-shell-surface px-2 text-shell-on",
          className,
        )}
      >
        {leading ? <div className="relative z-10 flex shrink-0 items-center">{leading}</div> : null}
        <div className="pointer-events-none absolute inset-x-0 flex items-center justify-center overflow-hidden px-14">
          {titleIcon ? <span className="mr-1.5 shrink-0 text-shell-on-secondary">{titleIcon}</span> : null}
          {title ? <span className="type-body-md min-w-0 truncate font-medium text-shell-on">{title}</span> : null}
        </div>
        {actions ? (
          <div className="relative z-10 ml-auto flex shrink-0 items-center gap-0.5">{actions}</div>
        ) : (
          <span className="ml-auto" />
        )}
      </header>
    );
  },
);
TopBar.displayName = "TopBar";

export interface TopBarButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible label — required: the button renders an icon only. */
  "aria-label": string;
}

/**
 * Ghost icon button for TopBar slots — 32px square, shell-coloured hover.
 * The Button atom's ghost sizes (h-8 with px, h-10 icon) don't fit the
 * 52px bar, so the shell owns this one small control.
 */
export const TopBarButton = forwardRef<HTMLButtonElement, TopBarButtonProps>(
  ({ className, type = "button", children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg text-shell-on-secondary outline-none transition-colors duration-150 hover:bg-surface-pressed hover:text-shell-on active:bg-surface-pressed focus-visible:ring-2 focus-visible:ring-border-focused",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
TopBarButton.displayName = "TopBarButton";
